import { DicomRoi } from '~/domain/dicom/dicomRoi';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { localization } from '~/i18n';

export type ValidationResult =
    | { valid: true }
    | { valid: false; error: string };

export function validateStructureNotApproved(
    structure: DicomRoi
): ValidationResult {
    if (structure.isApproved) {
        const error = structure.name
            ? localization.t(
                  'booleanOperations.validation.structureApprovedNamed',
                  {
                      name: structure.name,
                  }
              )
            : localization.t('booleanOperations.validation.structureApproved');
        return { valid: false, error };
    }
    return { valid: true };
}

export function validateStructureSetWritable(
    structureSet: StructureSetReadModel
): ValidationResult {
    if (structureSet.isApproved) {
        const error = structureSet.structureSetLabel
            ? localization.t(
                  'booleanOperations.validation.structureSetApprovedNamed',
                  {
                      name: structureSet.structureSetLabel,
                  }
              )
            : localization.t(
                  'booleanOperations.validation.structureSetApproved'
              );
        return { valid: false, error };
    }
    return { valid: true };
}

export function validateROIExists(
    roi: RegionOfInterest | null | undefined,
    errorMessage = localization.t(
        'booleanOperations.validation.structureNotFound'
    )
): ValidationResult {
    if (!roi) {
        return { valid: false, error: errorMessage };
    }
    return { valid: true };
}

export function validateROIHasTexture(
    roi: RegionOfInterest | null | undefined,
    structureName?: string
): ValidationResult {
    if (!roi) {
        return {
            valid: false,
            error: localization.t(
                'booleanOperations.validation.structureNotFound'
            ),
        };
    }
    if (!roi.texture) {
        const error = structureName
            ? localization.t(
                  'booleanOperations.validation.structureNoTextureNamed',
                  {
                      name: structureName,
                  }
              )
            : localization.t('booleanOperations.validation.structureNoTexture');
        return { valid: false, error };
    }

    const bbox = roi.texture.contentBoundingBox;
    const size = bbox.size;
    if (!size || size[0] <= 0 || size[1] <= 0 || size[2] <= 0) {
        const error = structureName
            ? localization.t(
                  'booleanOperations.validation.structureEmptyNamed',
                  {
                      name: structureName,
                  }
              )
            : localization.t('booleanOperations.validation.structureEmpty');
        return { valid: false, error };
    }
    return { valid: true };
}

/**
 * Validates that an ROI exists and has texture data.
 * This is a convenience function that combines both validations.
 */
export function validateROIAndTexture(
    roi: RegionOfInterest | null | undefined,
    structureName?: string
): ValidationResult {
    const existsValidation = validateROIExists(
        roi,
        structureName
            ? localization.t(
                  'booleanOperations.validation.structureNotFoundNamed',
                  {
                      name: structureName,
                  }
              )
            : localization.t('booleanOperations.validation.structureNotFound')
    );
    if (!existsValidation.valid) {
        return existsValidation;
    }
    return validateROIHasTexture(roi, structureName);
}

export function validateResultNameFormat(name: string): ValidationResult {
    const trimmed = name.trim();
    if (!trimmed) {
        return {
            valid: false,
            error: localization.t(
                'booleanOperations.validation.resultNameRequired'
            ),
        };
    }
    return { valid: true };
}
